import React, { useState, useEffect } from 'react'
import { AlertCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Button,
  Input,
  Select,
  SelectOption,
  Label,
} from '../ui'
import {
  useCreateDailyStaffingLimit,
  useUpdateDailyStaffingLimit,
  type DailyStaffingLimit,
  type CreateDailyStaffingLimitData,
  type UpdateDailyStaffingLimitData,
} from '../../hooks/useStaffingLimits'
import { usePositions } from '../../hooks/usePositions'
import { useDepartmentsForSelect } from '../../hooks/useDepartments'
import { useOrganizationsForSelect } from '../../hooks/organizations/useOrganizations'
import { LoadingSpinner } from '../ui/loading-states'

interface DailyStaffingLimitFormData {
  organization_id: string
  position_id: string
  department_id: string | null
  date: string
  max_employees: number
}

interface DailyStaffingLimitFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  limit?: DailyStaffingLimit | null
  mode: 'create' | 'edit'
}

export function DailyStaffingLimitForm({ open, onOpenChange, limit, mode }: DailyStaffingLimitFormProps) {
  const [formData, setFormData] = useState<DailyStaffingLimitFormData>({
    organization_id: '',
    position_id: '',
    department_id: null,
    date: '',
    max_employees: 1,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Load data for selection
  const { data: positionsData } = usePositions({ limit: 100 })
  const { data: organizationsData } = useOrganizationsForSelect()
  // Подразделения фильтруются по выбранной организации
  const { data: departmentsData } = useDepartmentsForSelect(formData.organization_id)

  const createLimit = useCreateDailyStaffingLimit()
  const updateLimit = useUpdateDailyStaffingLimit()

  const isLoading = createLimit.isPending || updateLimit.isPending

  // Initialize form data when dialog opens or limit changes
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && limit) {
        setFormData({
          organization_id: limit.organization_id || '',
          position_id: limit.position_id || '',
          department_id: limit.department_id || null,
          date: limit.date || '',
          max_employees: limit.max_employees || 1,
        })
      } else {
        // Default to today's date for new limits
        const today = new Date().toISOString().split('T')[0]
        setFormData({
          organization_id: organizationsData?.[0]?.id || '',
          position_id: '',
          department_id: null,
          date: today,
          max_employees: 1,
        })
      }
      setErrors({})
      createLimit.reset()
      updateLimit.reset()
    }
  }, [open, mode, limit, organizationsData])

  const handleInputChange = (field: keyof DailyStaffingLimitFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    let value: string | number | null = e.target.value

    // Convert empty string to null for department_id
    if (value === '' && field === 'department_id') {
      value = null
    }

    // Convert max_employees to number
    if (field === 'max_employees') {
      value = parseInt(e.target.value, 10) || 1
    }

    // При смене организации сбрасываем выбранное подразделение
    if (field === 'organization_id') {
      setFormData(prev => ({ ...prev, organization_id: value as string, department_id: null }))
      return
    }

    setFormData(prev => ({ ...prev, [field]: value }))

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.organization_id) {
      newErrors.organization_id = 'Организация обязательна'
    }

    if (!formData.position_id) {
      newErrors.position_id = 'Должность обязательна'
    }

    if (!formData.date) {
      newErrors.date = 'Дата обязательна'
    }

    if (!formData.max_employees || formData.max_employees < 1) {
      newErrors.max_employees = 'Лимит должен быть не менее 1'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      if (mode === 'create') {
        const data: CreateDailyStaffingLimitData = {
          organization_id: formData.organization_id,
          position_id: formData.position_id,
          department_id: formData.department_id || null,
          date: formData.date,
          max_employees: formData.max_employees,
        }
        await createLimit.mutateAsync(data)
      } else if (mode === 'edit' && limit) {
        const data: UpdateDailyStaffingLimitData = {
          organization_id: formData.organization_id,
          position_id: formData.position_id,
          department_id: formData.department_id,
          date: formData.date,
          max_employees: formData.max_employees,
        }
        await updateLimit.mutateAsync({ id: limit.id, data })
      }

      onOpenChange(false)
    } catch (error) {
      console.error('Form submission error:', error)
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Создать дневной лимит' : 'Редактировать дневной лимит'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Укажите организацию, должность, дату и максимальное количество сотрудников на смену.'
              : 'Измените параметры дневного лимита штатного расписания.'
            }
          </DialogDescription>
          {!isLoading && <DialogClose onClose={handleClose} />}
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Organization */}
          <div>
            <Label htmlFor="organization_id">Организация *</Label>
            <Select
              id="organization_id"
              value={formData.organization_id}
              onChange={handleInputChange('organization_id')}
              disabled={isLoading}
              className={errors.organization_id ? 'border-red-500' : ''}
            >
              <SelectOption value="">Выберите организацию</SelectOption>
              {organizationsData?.map((org: { id: string; name: string }) => (
                <SelectOption key={org.id} value={org.id}>
                  {org.name}
                </SelectOption>
              ))}
            </Select>
            {errors.organization_id && (
              <div className="flex items-center mt-1 text-sm text-red-600">
                <AlertCircle className="h-4 w-4 mr-1" />
                {errors.organization_id}
              </div>
            )}
          </div>

          {/* Position */}
          <div>
            <Label htmlFor="position_id">Должность *</Label>
            <Select
              id="position_id"
              value={formData.position_id}
              onChange={handleInputChange('position_id')}
              disabled={isLoading}
              className={errors.position_id ? 'border-red-500' : ''}
            >
              <SelectOption value="">Выберите должность</SelectOption>
              {positionsData?.data.map((position) => (
                <SelectOption key={position.id} value={position.id}>
                  {position.name}
                </SelectOption>
              ))}
            </Select>
            {errors.position_id && (
              <div className="flex items-center mt-1 text-sm text-red-600">
                <AlertCircle className="h-4 w-4 mr-1" />
                {errors.position_id}
              </div>
            )}
          </div>

          {/* Department (optional) */}
          <div>
            <Label htmlFor="department_id">Отдел (опционально)</Label>
            <Select
              id="department_id"
              value={formData.department_id || ''}
              onChange={handleInputChange('department_id')}
              disabled={isLoading || !formData.organization_id}
            >
              <SelectOption value="">
                {formData.organization_id ? 'Вся организация' : 'Сначала выберите организацию'}
              </SelectOption>
              {departmentsData?.map((dept) => (
                <SelectOption key={dept.id} value={dept.id}>
                  {dept.name}
                </SelectOption>
              ))}
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              Если не указан, лимит применяется ко всей организации
            </p>
          </div>

          {/* Date */}
          <div>
            <Label htmlFor="date">Дата *</Label>
            <Input
              id="date"
              type="date"
              value={formData.date}
              onChange={handleInputChange('date')}
              disabled={isLoading}
              className={errors.date ? 'border-red-500' : ''}
            />
            {errors.date && (
              <div className="flex items-center mt-1 text-sm text-red-600">
                <AlertCircle className="h-4 w-4 mr-1" />
                {errors.date}
              </div>
            )}
          </div>

          {/* Max Employees */}
          <div>
            <Label htmlFor="max_employees">Максимум сотрудников *</Label>
            <Input
              id="max_employees"
              type="number"
              min="1"
              value={formData.max_employees}
              onChange={handleInputChange('max_employees')}
              disabled={isLoading}
              className={errors.max_employees ? 'border-red-500' : ''}
            />
            {errors.max_employees && (
              <div className="flex items-center mt-1 text-sm text-red-600">
                <AlertCircle className="h-4 w-4 mr-1" />
                {errors.max_employees}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              Максимальное количество сотрудников данной должности, которое может работать в указанный день
            </p>
          </div>

          {/* Show API errors */}
          {(createLimit.error || updateLimit.error) && (
            <div className="flex items-center p-3 text-sm text-red-700 bg-red-100 rounded-md">
              <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>
                {(() => {
                  const error = (createLimit.error || updateLimit.error) as any
                  const message = error?.message
                  const status = error?.status

                  if (status === 409) {
                    return 'Дневной лимит для этой должности на указанную дату уже существует'
                  } else if (status === 400) {
                    return 'Проверьте правильность заполнения полей'
                  } else if (status === 403) {
                    return 'У вас нет прав для выполнения этой операции'
                  } else {
                    return message || `Произошла ошибка при ${mode === 'create' ? 'создании' : 'обновлении'} лимита`
                  }
                })()}
              </span>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Отменить
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="min-w-[100px]"
            >
              {isLoading ? (
                <>
                  <LoadingSpinner className="mr-2 h-4 w-4" />
                  {mode === 'create' ? 'Создание...' : 'Сохранение...'}
                </>
              ) : (
                mode === 'create' ? 'Создать' : 'Сохранить'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
